import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminArticles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'conseils',
    image_url: '',
    published: true,
  });

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/articles?published_only=false`, { withCredentials: true });
      setArticles(data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle.id}`, formData, { withCredentials: true });
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/articles`, formData, { withCredentials: true });
        toast.success('Article ajouté');
      }
      fetchArticles();
      resetForm();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await axios.delete(`${API}/articles/${id}`, { withCredentials: true });
      toast.success('Article supprimé');
      fetchArticles();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: 'conseils',
      image_url: '',
      published: true,
    });
    setEditingArticle(null);
    setShowModal(false);
  };

  const openEdit = (article) => {
    setFormData(article);
    setEditingArticle(article);
    setShowModal(true);
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (title) => {
    setFormData({ ...formData, title, slug: generateSlug(title) });
  };

  return (
    <AdminLayout title="Gestion des Articles">
      <button
        onClick={() => setShowModal(true)}
        className="btn-primary px-6 py-3 rounded-full mb-6 inline-flex items-center gap-2"
        data-testid="add-article-btn"
      >
        <Plus size={20} />
        Ajouter un article
      </button>

      {loading ? (
        <div>Chargement...</div>
      ) : (
        <div className="grid gap-4">
          {articles.map((article) => (
            <div key={article.id} className="bg-white p-4 rounded-lg border flex justify-between items-start" data-testid={`article-item-${article.id}`}>
              <div>
                <h3 className="text-lg font-medium text-[#5C2018]">{article.title}</h3>
                <p className="text-sm text-[#332724]">{article.excerpt}</p>
                <div className="flex gap-2 mt-2">
                  <span className="text-xs text-[#B8922A]">{article.category}</span>
                  <span className={`text-xs px-2 py-1 rounded ${article.published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {article.published ? 'Publié' : 'Brouillon'}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(article)} className="p-2 text-[#1E7A6A] hover:bg-[#1E7A6A]/10 rounded" data-testid={`edit-article-${article.id}`}>
                  <Edit2 size={18} />
                </button>
                <button onClick={() => handleDelete(article.id)} className="p-2 text-red-600 hover:bg-red-50 rounded" data-testid={`delete-article-${article.id}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={resetForm}>
          <div className="bg-white p-8 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="article-modal">
            <h2 className="text-2xl mb-4">{editingArticle ? 'Modifier' : 'Ajouter'} un article</h2>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                placeholder="Titre"
                value={formData.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="w-full p-2 border rounded mb-3"
                required
                data-testid="article-title-input"
              />
              <input
                type="text"
                placeholder="Slug (auto-généré)"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                className="w-full p-2 border rounded mb-3 bg-gray-50"
                required
              />
              <textarea
                placeholder="Extrait (résumé court)"
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                className="w-full p-2 border rounded mb-3"
                rows="2"
                required
              />
              <textarea
                placeholder="Contenu complet"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="w-full p-2 border rounded mb-3"
                rows="10"
                required
              />
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full p-2 border rounded mb-3"
              >
                <option value="conseils">Conseils</option>
                <option value="tendances">Tendances</option>
                <option value="histoire">Histoire</option>
                <option value="guide">Guides</option>
              </select>
              <input
                type="url"
                placeholder="URL de l'image"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="w-full p-2 border rounded mb-3"
              />
              <label className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                />
                <span className="text-sm">Publier immédiatement</span>
              </label>
              <div className="flex gap-3">
                <button type="submit" className="btn-primary px-6 py-2 rounded-full" data-testid="article-submit">
                  Enregistrer
                </button>
                <button type="button" onClick={resetForm} className="btn-secondary px-6 py-2 rounded-full">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminArticles;
